<template>
  <layout>
    <loader v-if="loading"></loader>
    <b-alert v-model="isError" variant="danger">{{ error }}</b-alert>
    <section v-if="!loading && item" class="profile">
      <box-info-main :item="item" />
      <div class="card">
        <div class="p-sm-4">
          <div>
            <b-tabs v-model="periodId" class="tab-solid tab-solid-primary">
              <b-tab
                v-for="(n, i) in filters"
                :key="i"
                :title="n"
                title-link-class="px-sm-5 py-sm-3"
              ></b-tab>
            </b-tabs>
            <div v-if="periodId == 4" class="row pl-0">
              <div class="col-xs-12 col-md-5 d-inline-flex p-3">
                <date-picker
                  v-model="timeStarts"
                  :default-value="timeStarts"
                  :disabled-date="disabledDatesStart"
                  type="datetime"
                  :show-time-panel="showTimePanel"
                  format="DD.MM.YYYY HH:mm"
                  :lang="locale"
                  :editable="false"
                  :clearable="false"
                  :disabled-time="disableTimeStart"
                  @close="handleOpenChange"
                >
                  <template #footer>
                    <button class="mx-btn mx-btn-text" @click="toggleTimePanel">
                      {{ showTimePanel ? $t('box.selectDate') : $t('box.selectTime') }}
                    </button>
                  </template>
                </date-picker>
                <span class="datepickerTextSize px-2">-</span>
                <date-picker
                  v-model="timeEnds"
                  :default-value="timeEnds"
                  :disabled-date="disabledDatesEnd"
                  type="datetime"
                  :show-time-panel="showTimePanel"
                  format="DD.MM.YYYY HH:mm"
                  :lang="locale"
                  :editable="false"
                  :clearable="false"
                  :disabled-time="disableTimeEnd"
                  @close="handleOpenChange"
                  popup-class="custom-select-time-popup-class"
                >
                  <template #footer>
                    <button class="mx-btn mx-btn-text" @click="toggleTimePanel">
                      {{ showTimePanel ? $t('box.selectDate') : $t('box.selectTime') }}
                    </button>
                  </template>
                </date-picker>
              </div>
            </div>
          </div>
          <states-chart
            :item="item"
            :period="period"
            :tab-period-text="tabPeriodText"
            :min-period="minTime"
            :max-period="maxTime"
            @stateId="getStateId"
          />
          <div>
            <h4 class="ml-3 pb-2 pt-4">
              {{ $t('boxInfo.workCenterState.title') + $t('dashboards.for') + tabPeriodText }}
            </h4>
            <b-tabs class="tab-solid tab-solid-primary" lazy>
              <b-tab :title="$t('box.byTime')">
                <event-chart
                  :work-center-id="workCenterId"
                  :period="period"
                  :state-id="stateId"
                  @changeViewPort="changeViewPort"
                />
              </b-tab>
              <b-tab v-if="hasShiftsFeature" :title="$t('box.byShifts')">
                <box-events-byshifts-chart :work-center-id="workCenterId" :period="period" />
              </b-tab>
              <b-tab v-if="hasShiftsFeature" :title="$t('box.statistic')">
                <states-chart
                  :item="item"
                  :period="period"
                  :tab-period-text="tabPeriodText"
                  by-shift
                  @stateId="getStateId"
                />
              </b-tab>
              <b-tab v-if="hasDowntimesFeature" :title="$t('capabilities.downtimeCategory')">
                <downtime-categorization-chart
                  :work-center-id="workCenterId"
                  :period="period"
                  :device-id="deviceIdInternal"
                />
              </b-tab>
            </b-tabs>
          </div>
          <div class="mt-3">
            <div class="row p-0 m-0">
              <b-tabs
                v-model="selectedSensorTab"
                class="tab-solid tab-solid-primary col-12 col-md p-0"
              >
                <b-tab v-for="(n, i) in sensors" :key="i" :title="n.name"></b-tab>
              </b-tabs>
              <div class="col-6 offset-7 col-md-auto offset-md-0 p-0">
                <sensor-values-report-form
                  :sensor-id="selectedSensorId"
                  :period="period"
                ></sensor-values-report-form>
              </div>
            </div>
            <sensor-canvas-plot
              :min-period="minTime"
              :max-period="maxTime"
              :sensor-id="selectedSensorId"
              :period="period"
            />
          </div>
          <div v-if="hasEnergyFeature" class="mt-3">
            <energy-consumption-plot
              :device-id="deviceIdInternal"
              :period="period"
              :tab-period-text="tabPeriodText"
              :min-period="minTime"
              :max-period="maxTime"
            />
          </div>
          <div v-if="boxEquipmentType && discreteSensorId" class="mt-3">
            <counter-values-plot
              :device-id="deviceIdInternal"
              :sensor-id="discreteSensorId"
              :box-equipment-type="boxEquipmentType"
              :period="period"
              :tab-period-text="tabPeriodText"
              :min-period="minTime"
              :max-period="maxTime"
            />
          </div>
          <div v-if="hasOperTimeFeature" class="mt-3">
            <box-operation-value-plot
              :device-id="deviceIdInternal"
              :period="period"
              :tab-period-text="tabPeriodText"
            />
          </div>
          <box-info-footer
            v-if="isUserAuthenticated"
            :item="item"
            :period="period"
            :reportCode="reportList[periodId]"
          />
        </div>
      </div>
    </section>
    <temporary-disabled-device-modal v-model="modalShow"></temporary-disabled-device-modal>
  </layout>
</template>

<script>
import boxInfo from '@/api/boxinfo/boxInfo.gql';
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/ru';
import 'vue2-datepicker/locale/en';
import 'vue2-datepicker/locale/pl';
import 'vue2-datepicker/locale/uk';
import { vueWindowSizeMixin } from 'vue-window-size';
import { DEVICE_STATUS } from '@/utils/const.js';

export default {
  name: 'BoxInfo',
  components: {
    DatePicker,
  },
  mixins: [vueWindowSizeMixin],
  props: {
    deviceId: String,
  },
  apollo: {
    item: {
      query: boxInfo,
      variables() {
        return {
          deviceId: this.deviceIdInternal,
          sensorsViewType: 'ALL',
        };
      },
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        let deviceStatus = queryData?.boxes[0]?.deviceStatus || null;
        this.openTemporaryDisabledDeviceModal(deviceStatus);
        return ((queryData || {}).boxes || [])[0] || null;
      },
      skip() {
        return !this.deviceIdInternal || !this.isDeviceIdCorrect;
      },
      error() {
        this.$router.push({ name: 'MaintenanceOee' });
      },
      pollInterval: 31000,
      fetchPolicy: 'cache-and-network',
    },
  },
  data() {
    return {
      showTimePanel: false,
      minTime: 0,
      maxTime: 0,
      timeStart: moment().subtract(9, 'days').startOf('day').toDate(),
      timeEnd: moment().hour(23).minute(59).toDate(),
      today: new Date(),
      stateId: '',
      periodId: 0,
      selectedSensorTab: 0,
      reportList: ['DAY', 'DAY', 'WEEK', 'MONTH', 'PERIOD'],
      modalShow: false,
    };
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    timeEnds: {
      get: function () {
        return this.timeEnd || new Date();
      },
      set: function (v) {
        if (v < this.timeStart) {
          v = moment(this.timeStart).add(1, 'h').toDate();
        }
        this.timeEnd = v;
      },
    },
    isError() {
      return !!this.error;
    },
    timeStarts: {
      get: function () {
        return this.timeStart || new Date();
      },
      set: function (v) {
        if (this.timeEnd < v) {
          v = moment(this.timeEnd).subtract(1, 'h').toDate();
        }
        this.timeStart = v;
      },
    },
    error() {
      if (!this.isDeviceIdCorrect) {
        return this.$t('box.idNotCorrect');
      } else if (this.item === null) {
        return this.$t('box.equipmentNotFound');
      }
      return null;
    },
    tabPeriodText() {
      switch (this.periodId) {
        case 0:
          return this.$t('dashboards.today').toLowerCase();
        case 1:
          return this.$t('dashboards.yesterday').toLowerCase();
        case 2:
          return this.$t('dashboards.weeks').toLowerCase();
        case 3:
          return this.$t('dashboards.month').toLowerCase();
        case 4:
          return `${moment(this.period.dateStart).format('DD.MM.YYYY HH:mm')} - ${moment(
            this.period.dateEnd,
          ).format('DD.MM.YYYY HH:mm')}`;
        default:
          return '';
      }
    },
    filters() {
      return [
        this.$t('dashboards.today'),
        this.$t('dashboards.yesterday'),
        this.$t('dashboards.week'),
        this.$t('dashboards.month'),
        this.$t('dashboards.period'),
      ];
    },
    loading() {
      return this.$apollo.queries.item.loading;
    },
    isDeviceIdCorrect() {
      const guidRegexp =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return guidRegexp.test(this.deviceIdInternal);
    },
    deviceIdInternal() {
      return this.deviceId ? this.deviceId : this.$route.params.deviceId;
    },
    period() {
      switch (this.periodId) {
        case 0:
          return {
            dateStart: moment().startOf('day').format(),
          };
        case 1:
          return {
            dateStart: moment().startOf('day').subtract(1, 'days').format(),
            dateEnd: moment().endOf('day').subtract(1, 'days').format(),
          };
        case 2:
          return {
            dateStart: moment().startOf('day').subtract(7, 'days').format(),
          };
        case 3:
          return {
            dateStart: moment().startOf('day').subtract(31, 'days').format(),
          };
        case 4:
          return this.dateStart > this.dateEnd
            ? this.showDateError
            : {
                dateStart: moment(this.timeStart, 'DD.MM.YYYY HH:mm').format(),
                dateEnd: moment(this.timeEnd, 'DD.MM.YYYY HH:mm').format(),
              };
        default:
          return {
            dateStart: moment().startOf('day').format(),
          };
      }
    },
    boxEquipmentType() {
      return (this.item || {}).boxEquipmentType || 0;
    },
    workCenterId() {
      return (((this.item || {}).digitalThing || {}).workCenter || {}).id;
    },
    discreteSensorId() {
      const sensors = ((this.item || {}).digitalThing || {}).sensors || [];
      return (sensors.find((s) => s.type && s.type.id == 10) || {}).id || 0;
    },
    operModeSensorId() {
      return (
        ((((this.item || {}).digitalThing || {}).workCenter || {}).operationModeSensor || {}).id ||
        null
      );
    },
    batterySensorId() {
      return ((this.item || {}).batterySensor || {}).id || null;
    },
    sensors() {
      const sensors = ((this.item || {}).digitalThing || {}).sensors || [];
      return sensors.filter(
        (s) =>
          s.type &&
          (s.type.id == '1' ||
            s.type.id == '12' ||
            s.type.id == '13' ||
            s.type.id == '4' ||
            s.type.id == 'STMP' ||
            s.type.id == 'VIBR' ||
            s.type.id == 'SPRES' ||
            s.type.id == 'BUTT'),
      );
    },
    selectedSensorId() {
      return (this.sensors[this.selectedSensorTab] || {}).id || 0;
    },
    targetEfficiency() {
      return (
        ((((this.item || {}).digitalThing || {}).workCenter || {}).efficiencyParam || {})
          .targetEfficiency || 85
      );
    },
    isUserAuthenticated() {
      return this.$authCore.isAuthenticated;
    },
    hasDowntimesFeature() {
      return this.hasBoxFeature(this.deviceIdInternal, 'downtimes');
    },
    hasShiftsFeature() {
      return this.hasBoxFeature(this.deviceIdInternal, 'shifts');
    },
    hasEnergyFeature() {
      return this.hasBoxFeature(this.deviceIdInternal, 'ems');
    },
    hasOperTimeFeature() {
      return this.hasBoxFeature(this.deviceIdInternal, 'optime');
    },
  },
  methods: {
    openTemporaryDisabledDeviceModal(deviceStatus) {
      this.modalShow = deviceStatus == DEVICE_STATUS.TEMPORARY_DISABLED_DEVICE_STATUS;
    },
    toggleTimePanel() {
      this.showTimePanel = !this.showTimePanel;
    },
    handleOpenChange() {
      this.showTimePanel = false;
    },
    changeViewPort(min, max) {
      this.minTime = min;
      this.maxTime = max;
    },
    getStateId(id) {
      this.stateId = id;
    },
    disableTimeEnd(date) {
      if (
        this.timeEnd.getDay() == this.timeStart.getDay() &&
        this.timeStart.getDay() == date.getDay() &&
        this.timeEnd.getDay() == date.getDay()
      ) {
        return date < this.timeStart || date > new Date();
      }
    },
    disableTimeStart(date) {
      if (
        this.timeEnd.getDay() == this.timeStart.getDay() &&
        this.timeStart.getDay() == date.getDay() &&
        this.timeEnd.getDay() == date.getDay()
      ) {
        return date < this.item.startDate || date > this.timeEnd;
      }
    },
    disabledDatesEnd(date) {
      return date < new Date(this.timeStart).setHours(0, 0, 0, 0) || date > new Date();
    },
    disabledDatesStart(date) {
      return (
        date < new Date(this.item.startDate).setHours(0, 0, 0, 0) ||
        date > new Date(this.timeEnd).setHours(0, 0, 0, 0)
      );
    },
    showDateError() {
      this.$bvToast.toast('Начальная дата не должна превышать конечную дату', {
        title: this.$t('toast.error'),
        variant: 'danger',
        solid: true,
      });
    },
  },
};
</script>

<style scoped>
.tab-solid {
  margin-bottom: 0px !important;
}
.datepickerTextSize {
  font-size: 2rem;
}
@media (max-width: 600px) {
  .reverse {
    flex-direction: column-reverse;
  }
}
</style>
